<template>
  <div class="weeduc-admin-page weeduc-integrations-page text-start p-0">
    <header class="weeduc-admin-page__header">
      <div class="weeduc-admin-page__header-lead">
        <TitlePage
          :title="$t('admin.apiIntegrations.title')"
          :subtitle="$t('admin.apiIntegrations.subtitle')"
          :hasMargin="false"
        />
      </div>
    </header>

    <div class="weeduc-admin-page__shell">
      <section class="weeduc-admin-page__panel weeduc-integrations-panel weeduc-integrations-panel--token">
        <div class="weeduc-integrations-token-layout">
          <div>
            <h2 class="weeduc-integrations-panel__title">
              {{ $t('admin.apiIntegrations.tokenTitle') }}
            </h2>
            <p class="weeduc-integrations-panel__lead">
              {{ $t('admin.apiIntegrations.tokenLead') }}
            </p>

            <div class="weeduc-integrations-token">
              <label class="weeduc-integrations-label" for="apiIntegrationToken">
                {{ $t('admin.apiIntegrations.apiToken') }}
              </label>
              <div class="weeduc-integrations-token__row">
                <input
                  id="apiIntegrationToken"
                  class="weeduc-integrations-token__input"
                  type="text"
                  readonly
                  :value="tokenVisible ? apiToken : maskedToken"
                  autocomplete="off"
                />
                <button
                  type="button"
                  class="weeduc-integrations-btn weeduc-integrations-btn--ghost"
                  :title="tokenVisible ? $t('admin.apiIntegrations.hideToken') : $t('admin.apiIntegrations.showToken')"
                  @click="tokenVisible = !tokenVisible"
                >
                  <fa :icon="tokenVisible ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" />
                </button>
                <button
                  type="button"
                  class="weeduc-integrations-btn weeduc-integrations-btn--ghost"
                  :title="$t('admin.apiIntegrations.copyToken')"
                  @click="copyToken"
                >
                  <fa icon="fa-solid fa-copy" />
                </button>
              </div>
              <p class="weeduc-integrations-hint">
                {{ $t('admin.apiIntegrations.tokenHint') }}
              </p>
            </div>

            <div class="weeduc-integrations-actions">
              <button
                type="button"
                class="weeduc-integrations-btn weeduc-integrations-btn--primary"
                :disabled="busy"
                @click="regenerateToken"
              >
                {{ busy === 'regen' ? $t('admin.apiIntegrations.generating') : $t('admin.apiIntegrations.regenerate') }}
              </button>
              <button
                type="button"
                class="weeduc-integrations-btn weeduc-integrations-btn--danger"
                :disabled="busy || !apiToken"
                @click="revokeToken"
              >
                {{ busy === 'revoke' ? $t('admin.apiIntegrations.revoking') : $t('admin.apiIntegrations.revoke') }}
              </button>
            </div>
          </div>

          <div class="weeduc-integrations-meta">
            <div>
              <span>{{ $t('admin.apiIntegrations.status') }}</span>
              <strong :class="apiToken ? 'is-active' : 'is-inactive'">
                {{ apiToken ? $t('admin.apiIntegrations.statusActive') : $t('admin.apiIntegrations.statusInactive') }}
              </strong>
            </div>
            <div>
              <span>{{ $t('admin.apiIntegrations.createdAt') }}</span>
              <strong>{{ createdAtLabel }}</strong>
            </div>
            <div>
              <span>{{ $t('admin.apiIntegrations.lastUsed') }}</span>
              <strong>{{ lastUsedLabel }}</strong>
            </div>
          </div>
        </div>
      </section>

      <section class="weeduc-admin-page__panel weeduc-integrations-panel">
        <nav
          class="weeduc-admin-tabs"
          :aria-label="$t('admin.apiIntegrations.tabsAria')"
        >
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="weeduc-admin-tab"
            :class="{ 'weeduc-admin-tab--active': activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </nav>

        <div
          v-for="tab in tabs"
          :key="`panel-${tab.id}`"
          v-show="activeTab === tab.id"
          class="weeduc-integrations-tab-panel"
        >
          <h2 class="weeduc-integrations-panel__title">
            {{ tab.title }}
          </h2>
          <p class="weeduc-integrations-panel__lead">
            {{ tab.lead }}
          </p>

          <div class="weeduc-integrations-endpoint-cards">
            <article
              v-for="endpoint in tab.endpoints"
              :key="endpoint.id"
              class="weeduc-integrations-endpoint-card"
              :class="{ 'is-selected': selectedEndpointId === endpoint.id }"
              @click="selectedEndpointId = endpoint.id"
            >
              <div class="weeduc-integrations-endpoint-card__head">
                <span
                  class="weeduc-integrations-method"
                  :data-method="endpoint.method"
                >
                  {{ endpoint.method }}
                </span>
                <span
                  class="weeduc-integrations-resource__badge"
                  :data-resource="endpoint.resource"
                >
                  {{ resourceLabel(endpoint.resource) }}
                </span>
              </div>

              <p class="weeduc-integrations-endpoint-card__name">
                {{ endpoint.name }}
              </p>
              <p class="weeduc-integrations-endpoint-card__desc">
                {{ endpoint.description }}
              </p>

              <div class="weeduc-integrations-endpoint__row">
                <code class="weeduc-integrations-code">{{ endpoint.path }}</code>
                <button
                  type="button"
                  class="weeduc-integrations-btn weeduc-integrations-btn--ghost"
                  :title="$t('admin.apiIntegrations.copyEndpoint')"
                  @click.stop="copyText(endpoint.path, 'endpoint')"
                >
                  <fa icon="fa-solid fa-copy" />
                </button>
              </div>

              <div
                v-if="endpoint.bodyExample"
                class="weeduc-integrations-body"
              >
                <p class="weeduc-integrations-label">
                  {{ $t('admin.apiIntegrations.bodyExample') }}
                </p>
                <pre class="weeduc-integrations-pre">{{ endpoint.bodyExample }}</pre>
              </div>

              <div class="weeduc-integrations-endpoint-card__actions">
                <button
                  type="button"
                  class="weeduc-integrations-btn weeduc-integrations-btn--primary"
                  :disabled="busy || !apiToken"
                  @click.stop="simulateRequest(endpoint)"
                >
                  {{ busy === endpoint.id
                    ? $t('admin.apiIntegrations.requesting')
                    : $t('admin.apiIntegrations.simulateRequest') }}
                </button>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="weeduc-admin-page__panel weeduc-integrations-panel weeduc-integrations-panel--table">
        <div class="weeduc-integrations-table-head">
          <div>
            <h2 class="weeduc-integrations-panel__title">
              {{ $t('admin.apiIntegrations.historyTitle') }}
            </h2>
            <p class="weeduc-integrations-panel__lead">
              {{ $t('admin.apiIntegrations.historyLead') }}
            </p>
          </div>
          <span class="weeduc-integrations-badge">{{ $t('admin.apiIntegrations.mockBadge') }}</span>
        </div>

        <div class="weeduc-admin-page__table-section">
          <v-table class="custom-table">
            <thead>
              <tr>
                <th>{{ $t('admin.apiIntegrations.colDate') }}</th>
                <th>{{ $t('admin.apiIntegrations.colResource') }}</th>
                <th>{{ $t('admin.apiIntegrations.colMethod') }}</th>
                <th>{{ $t('admin.apiIntegrations.colRecords') }}</th>
                <th>{{ $t('admin.apiIntegrations.colStatus') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in requestHistory" :key="item.id">
                <td>{{ item.date }}</td>
                <td>
                  <span class="weeduc-integrations-resource__badge" :data-resource="item.resource">
                    {{ resourceLabel(item.resource) }}
                  </span>
                </td>
                <td>
                  <span class="weeduc-integrations-method" :data-method="item.method">
                    {{ item.method }}
                  </span>
                </td>
                <td>{{ item.records }}</td>
                <td>
                  <span
                    class="weeduc-integrations-status"
                    :class="item.status === 'success' ? 'is-success' : 'is-error'"
                  >
                    {{ item.status === 'success'
                      ? $t('admin.apiIntegrations.statusSuccess')
                      : $t('admin.apiIntegrations.statusError') }}
                  </span>
                </td>
              </tr>
              <tr v-if="!requestHistory.length">
                <td colspan="5" class="text-center text-medium-emphasis">
                  {{ $t('admin.apiIntegrations.historyEmpty') }}
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import TitlePage from '@/components/Weflix/titlePage.vue';
import { toast } from 'vue3-toastify';

const MOCK_TOKEN = 'weed_live_8f3a2c91b7e04d65a1c0e9f2b4d8a376';
const API_BASE = 'https://api.weeduc.com.br/v1/integrations';

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(value instanceof Date ? value : new Date(value));
  } catch {
    return String(value);
  }
}

function prettyJson(value) {
  return JSON.stringify(value, null, 2);
}

export default {
  name: 'WeeducAdminIntegrationsApi',
  components: { TitlePage },
  data() {
    return {
      apiToken: MOCK_TOKEN,
      tokenVisible: false,
      createdAt: new Date('2026-09-10T14:30:00'),
      lastUsed: new Date('2026-09-22T11:05:00'),
      activeTab: 'import',
      selectedEndpointId: 'import-clients',
      busy: null,
      requestHistory: [
        {
          id: 1,
          date: '22/09/2026 11:05',
          resource: 'clients',
          method: 'POST',
          records: 128,
          status: 'success',
        },
        {
          id: 2,
          date: '18/09/2026 09:42',
          resource: 'courses',
          method: 'POST',
          records: 12,
          status: 'success',
        },
        {
          id: 3,
          date: '12/09/2026 16:18',
          resource: 'reports',
          method: 'POST',
          records: 0,
          status: 'error',
        },
      ],
    };
  },
  computed: {
    maskedToken() {
      if (!this.apiToken) return '';
      const visible = this.apiToken.slice(0, 10);
      return `${visible}${'•'.repeat(Math.max(12, this.apiToken.length - 10))}`;
    },
    createdAtLabel() {
      return formatDateTime(this.createdAt);
    },
    lastUsedLabel() {
      return formatDateTime(this.lastUsed);
    },
    tabs() {
      return [
        {
          id: 'import',
          label: this.$t('admin.apiIntegrations.tabImport'),
          title: this.$t('admin.apiIntegrations.tabImportTitle'),
          lead: this.$t('admin.apiIntegrations.tabImportLead'),
          endpoints: [
            {
              id: 'import-clients',
              resource: 'clients',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epImportClients'),
              description: this.$t('admin.apiIntegrations.epImportClientsDesc'),
              path: `${API_BASE}/import/clients`,
              bodyExample: prettyJson({
                sourceSystem: 'external-lms',
                page: 1,
                pageSize: 100,
              }),
            },
            {
              id: 'import-courses',
              resource: 'courses',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epImportCourses'),
              description: this.$t('admin.apiIntegrations.epImportCoursesDesc'),
              path: `${API_BASE}/import/courses`,
              bodyExample: prettyJson({
                sourceSystem: 'external-lms',
                page: 1,
                pageSize: 50,
              }),
            },
          ],
        },
        {
          id: 'courses',
          label: this.$t('admin.apiIntegrations.tabCourses'),
          title: this.$t('admin.apiIntegrations.tabCoursesTitle'),
          lead: this.$t('admin.apiIntegrations.tabCoursesLead'),
          endpoints: [
            {
              id: 'courses-list',
              resource: 'courses',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epCoursesList'),
              description: this.$t('admin.apiIntegrations.epCoursesListDesc'),
              path: `${API_BASE}/courses/list`,
              bodyExample: prettyJson({
                status: 'active',
                search: '',
                page: 1,
                pageSize: 50,
              }),
            },
          ],
        },
        {
          id: 'users',
          label: this.$t('admin.apiIntegrations.tabUsers'),
          title: this.$t('admin.apiIntegrations.tabUsersTitle'),
          lead: this.$t('admin.apiIntegrations.tabUsersLead'),
          endpoints: [
            {
              id: 'users-students',
              resource: 'students',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epStudentsList'),
              description: this.$t('admin.apiIntegrations.epStudentsListDesc'),
              path: `${API_BASE}/users/students/list`,
              bodyExample: prettyJson({
                search: '',
                status: 'active',
                page: 1,
                pageSize: 50,
              }),
            },
            {
              id: 'users-collaborators',
              resource: 'collaborators',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epCollaboratorsList'),
              description: this.$t('admin.apiIntegrations.epCollaboratorsListDesc'),
              path: `${API_BASE}/users/collaborators/list`,
              bodyExample: prettyJson({
                search: '',
                departmentId: null,
                page: 1,
                pageSize: 50,
              }),
            },
            {
              id: 'users-all',
              resource: 'users',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epUsersList'),
              description: this.$t('admin.apiIntegrations.epUsersListDesc'),
              path: `${API_BASE}/users/list`,
              bodyExample: prettyJson({
                roles: ['student', 'collaborator', 'admin'],
                page: 1,
                pageSize: 50,
              }),
            },
          ],
        },
        {
          id: 'reports',
          label: this.$t('admin.apiIntegrations.tabReports'),
          title: this.$t('admin.apiIntegrations.tabReportsTitle'),
          lead: this.$t('admin.apiIntegrations.tabReportsLead'),
          endpoints: [
            {
              id: 'reports-courses',
              resource: 'reports',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epReportsCourses'),
              description: this.$t('admin.apiIntegrations.epReportsCoursesDesc'),
              path: `${API_BASE}/reports/courses`,
              bodyExample: prettyJson({
                courseId: null,
                dateFrom: '2026-01-01',
                dateTo: '2026-09-23',
              }),
            },
            {
              id: 'reports-enrolments',
              resource: 'reports',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epReportsEnrolments'),
              description: this.$t('admin.apiIntegrations.epReportsEnrolmentsDesc'),
              path: `${API_BASE}/reports/enrolments`,
              bodyExample: prettyJson({
                courseId: null,
                status: 'all',
                page: 1,
                pageSize: 100,
              }),
            },
            {
              id: 'reports-progress',
              resource: 'reports',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epReportsProgress'),
              description: this.$t('admin.apiIntegrations.epReportsProgressDesc'),
              path: `${API_BASE}/reports/progress`,
              bodyExample: prettyJson({
                courseId: null,
                minProgress: 0,
                maxProgress: 100,
              }),
            },
          ],
        },
      ];
    },
  },
  watch: {
    activeTab(tabId) {
      const tab = this.tabs.find((item) => item.id === tabId);
      if (tab?.endpoints?.length) {
        this.selectedEndpointId = tab.endpoints[0].id;
      }
    },
  },
  methods: {
    resourceLabel(id) {
      const map = {
        clients: 'resourceClients',
        courses: 'resourceCourses',
        students: 'resourceStudents',
        collaborators: 'resourceCollaborators',
        users: 'resourceUsers',
        reports: 'resourceReports',
      };
      const key = map[id] || 'resourceClients';
      return this.$t(`admin.apiIntegrations.${key}`);
    },
    async copyToken() {
      if (!this.apiToken) return;
      await this.copyText(this.apiToken, 'token');
    },
    async copyText(value, kind) {
      try {
        await navigator.clipboard.writeText(value);
        toast.success(
          kind === 'token'
            ? this.$t('admin.apiIntegrations.copySuccess')
            : this.$t('admin.apiIntegrations.copyEndpointSuccess'),
        );
      } catch {
        toast.error(this.$t('admin.apiIntegrations.copyError'));
      }
    },
    async regenerateToken() {
      this.busy = 'regen';
      await new Promise((resolve) => setTimeout(resolve, 700));
      const suffix = Math.random().toString(16).slice(2, 18);
      this.apiToken = `weed_live_${suffix}`;
      this.createdAt = new Date();
      this.lastUsed = null;
      this.tokenVisible = true;
      this.busy = null;
      toast.success(this.$t('admin.apiIntegrations.regenSuccess'));
    },
    async revokeToken() {
      this.busy = 'revoke';
      await new Promise((resolve) => setTimeout(resolve, 500));
      this.apiToken = '';
      this.tokenVisible = false;
      this.lastUsed = null;
      this.busy = null;
      toast.info(this.$t('admin.apiIntegrations.revokeSuccess'));
    },
    async simulateRequest(endpoint) {
      this.selectedEndpointId = endpoint.id;
      this.busy = endpoint.id;
      await new Promise((resolve) => setTimeout(resolve, 900));
      const records = Math.floor(Math.random() * 90) + 10;
      const now = new Date();
      this.lastUsed = now;
      this.requestHistory.unshift({
        id: Date.now(),
        date: formatDateTime(now),
        resource: endpoint.resource,
        method: endpoint.method,
        records,
        status: 'success',
      });
      this.busy = null;
      toast.success(
        this.$t('admin.apiIntegrations.requestSuccess', {
          count: records,
          resource: this.resourceLabel(endpoint.resource),
        }),
      );
    },
  },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/dashboard-weflix-shell.css';

.weeduc-integrations-panel--token {
  margin-bottom: 1.15rem;
}

.weeduc-integrations-token-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(220px, 0.8fr);
  gap: 1.5rem;
  align-items: start;
}

.weeduc-integrations-panel__title {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1d26;
}

.weeduc-integrations-panel__lead {
  margin: 0 0 1.15rem;
  font-size: 0.875rem;
  color: #697386;
  line-height: 1.45;
}

.weeduc-integrations-label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
}

.weeduc-integrations-token__row,
.weeduc-integrations-endpoint__row {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.weeduc-integrations-token__input {
  flex: 1;
  min-width: 0;
  padding: 0.7rem 0.9rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.8125rem;
  color: #1f2937;
  background: #f9fafb;
}

.weeduc-integrations-hint {
  margin: 0.45rem 0 0;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
}

.weeduc-integrations-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 1rem;
}

.weeduc-integrations-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0.55rem 0.95rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.weeduc-integrations-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.weeduc-integrations-btn--primary {
  background: #250891;
  color: #fff;
}

.weeduc-integrations-btn--primary:hover:not(:disabled) {
  background: #1d0673;
}

.weeduc-integrations-btn--danger {
  background: #fff;
  color: #b91c1c;
  border-color: #fecaca;
}

.weeduc-integrations-btn--danger:hover:not(:disabled) {
  background: #fef2f2;
}

.weeduc-integrations-btn--ghost {
  background: #fff;
  color: #250891;
  border-color: #d1d5db;
  min-width: 2.5rem;
  padding-left: 0.7rem;
  padding-right: 0.7rem;
}

.weeduc-integrations-btn--ghost:hover:not(:disabled) {
  background: #f3f0ff;
  border-color: #250891;
  color: #1d0673;
}

.weeduc-integrations-meta {
  display: grid;
  gap: 0.85rem;
  padding: 1rem;
  border: 1px solid #e8eaf0;
  border-radius: 10px;
  background: #f9fafb;
}

.weeduc-integrations-meta span {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
  margin-bottom: 0.2rem;
}

.weeduc-integrations-meta strong {
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 600;
}

.weeduc-integrations-meta strong.is-active {
  color: #047857;
}

.weeduc-integrations-meta strong.is-inactive {
  color: #b91c1c;
}

.weeduc-integrations-tab-panel {
  margin-top: 1.15rem;
}

.weeduc-integrations-endpoint-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.95rem;
}

.weeduc-integrations-endpoint-card {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1rem;
  border: 1px solid #e8eaf0;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  text-align: left;
}

.weeduc-integrations-endpoint-card.is-selected {
  border-color: #250891;
  background: #f8f7ff;
}

.weeduc-integrations-endpoint-card__head {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  align-items: center;
}

.weeduc-integrations-endpoint-card__name {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
}

.weeduc-integrations-endpoint-card__desc {
  margin: 0;
  font-size: 0.8125rem;
  color: #697386;
  line-height: 1.4;
}

.weeduc-integrations-endpoint-card__actions {
  margin-top: auto;
  padding-top: 0.35rem;
}

.weeduc-integrations-method {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 6px;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  background: #dcfce7;
  color: #166534;
}

.weeduc-integrations-method[data-method='GET'] {
  background: #dbeafe;
  color: #1d4ed8;
}

.weeduc-integrations-resource__badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  background: #f3f4f6;
  color: #4b5563;
}

.weeduc-integrations-resource__badge[data-resource='clients'],
.weeduc-integrations-resource__badge[data-resource='users'] {
  background: #ede9fe;
  color: #5b21b6;
}

.weeduc-integrations-resource__badge[data-resource='courses'] {
  background: #dbeafe;
  color: #1d4ed8;
}

.weeduc-integrations-resource__badge[data-resource='students'] {
  background: #ccfbf1;
  color: #0f766e;
}

.weeduc-integrations-resource__badge[data-resource='collaborators'] {
  background: #ffedd5;
  color: #c2410c;
}

.weeduc-integrations-resource__badge[data-resource='reports'] {
  background: #fce7f3;
  color: #be185d;
}

.weeduc-integrations-code {
  display: block;
  flex: 1;
  min-width: 0;
  padding: 0.7rem 0.9rem;
  border-radius: 8px;
  background: #111827;
  color: #e5e7eb;
  font-size: 0.72rem;
  word-break: break-all;
}

.weeduc-integrations-body {
  margin-top: 0.15rem;
}

.weeduc-integrations-pre {
  margin: 0;
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  background: #f3f4f6;
  color: #374151;
  font-size: 0.72rem;
  line-height: 1.45;
  overflow-x: auto;
}

.weeduc-integrations-table-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.35rem;
}

.weeduc-integrations-badge {
  flex-shrink: 0;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.weeduc-integrations-status {
  display: inline-flex;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.weeduc-integrations-status.is-success {
  background: #d1fae5;
  color: #047857;
}

.weeduc-integrations-status.is-error {
  background: #fee2e2;
  color: #b91c1c;
}

.weeduc-integrations-panel--table {
  margin-top: 1.15rem;
}

@media (max-width: 960px) {
  .weeduc-integrations-token-layout {
    grid-template-columns: 1fr;
  }
}
</style>
